import React, { useEffect, useState } from 'react';
import { Loader2, User as UserIcon, X } from 'lucide-react';
import { atlasApi, AtlasUser, Colaborador, DiscGrupo, DiscResposta } from '../shared/atlasApi';
import PassosQuestionario from './PassosQuestionario';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
  /** funccodi de quem foi respondido quando o RH escolheu alguém. */
  onConcluir: (funccodi?: number) => void;
  onCancelar: () => void;
}

const Questionario: React.FC<Props> = ({ user, podeEditar, onConcluir, onCancelar }) => {
  const [grupos, setGrupos] = useState<DiscGrupo[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Seleção de colaborador alvo (apenas para admin/RH; user comum responde pelo próprio email)
  const [alvo, setAlvo] = useState<{ funccodi: number; nome: string } | null>(null);
  const [buscaAlvo, setBuscaAlvo] = useState('');
  const [resultadosAlvo, setResultadosAlvo] = useState<Colaborador[]>([]);
  const [buscandoAlvo, setBuscandoAlvo] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    atlasApi.discQuestionario(user)
      .then(r => { if (!cancel) setGrupos(r.grupos || []); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar questionário'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user]);

  // Busca de colaboradores (apenas quando admin/RH precisa selecionar alvo)
  useEffect(() => {
    if (!podeEditar || alvo) return;
    const termo = buscaAlvo.trim();
    if (termo.length < 2) { setResultadosAlvo([]); return; }
    let cancel = false;
    setBuscandoAlvo(true);
    const t = window.setTimeout(() => {
      atlasApi.listarColaboradores(user, { busca: termo, status: 'ativo', per_page: 8 })
        .then((r: any) => { if (!cancel) setResultadosAlvo(r?.itens || []); })
        .catch(() => { if (!cancel) setResultadosAlvo([]); })
        .finally(() => { if (!cancel) setBuscandoAlvo(false); });
    }, 300);
    return () => { cancel = true; window.clearTimeout(t); };
  }, [user, podeEditar, alvo, buscaAlvo]);

  const concluir = async (payload: DiscResposta[]) => {
    setSalvando(true);
    setErro(null);
    try {
      await atlasApi.discSalvarRespostas(user, {
        respostas: payload,
        ...(alvo ? { funccodi: alvo.funccodi } : {}),
      });
      onConcluir(alvo?.funccodi);
    } catch (e: any) {
      setErro(e.message || 'Erro ao salvar respostas.');
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        Carregando questionário...
      </div>
    );
  }

  if (erro && grupos.length === 0) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700 max-w-2xl mx-auto">{erro}</div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Seletor de colaborador alvo (somente admin/RH) */}
      {podeEditar && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <UserIcon className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-semibold text-gray-700">Respondendo em nome de</span>
          </div>
          {alvo ? (
            <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-purple-50 border border-purple-200">
              <div className="min-w-0">
                <div className="text-sm font-medium text-purple-900 truncate">{alvo.nome}</div>
                <div className="text-xs text-purple-700">Mat. {alvo.funccodi}</div>
              </div>
              <button
                onClick={() => { setAlvo(null); setBuscaAlvo(''); }}
                className="p-1.5 rounded-md text-purple-700 hover:bg-purple-100"
                title="Trocar colaborador"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                value={buscaAlvo}
                onChange={ev => setBuscaAlvo(ev.target.value)}
                placeholder="Buscar colaborador por nome (mín. 2 letras)..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
              />
              {buscandoAlvo && (
                <div className="text-xs text-gray-400 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> buscando...
                </div>
              )}
              {!buscandoAlvo && resultadosAlvo.length > 0 && (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-auto">
                  {resultadosAlvo.map(c => (
                    <button
                      key={c._id}
                      onClick={() => setAlvo({ funccodi: c.funccodi, nome: c.dados_publicos?.nome || `#${c.funccodi}` })}
                      className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm"
                    >
                      <div className="font-medium text-gray-800 truncate">{c.dados_publicos?.nome || '—'}</div>
                      <div className="text-xs text-gray-500 truncate">
                        Mat. {c.dados_publicos?.matricula || c.funccodi}
                        {c.dados_publicos?.cargo_nome ? ` · ${c.dados_publicos.cargo_nome}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-400">
                Não selecionando ninguém, o sistema tenta vincular pelo seu e-mail corporativo.
              </p>
            </div>
          )}
        </div>
      )}

      <PassosQuestionario
        grupos={grupos}
        salvando={salvando}
        erro={erro}
        onConcluir={concluir}
        onCancelar={onCancelar}
      />
    </div>
  );
};

export default Questionario;
